import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { APIClient } from '../../helpers/api_helper';

const api = new APIClient();

/**
 * Generate a simple session ID that persists until tab is closed
 */
const getSessionId = () => {
  let sid = sessionStorage.getItem('_lw_session_id');
  if (!sid) {
    sid = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    sessionStorage.setItem('_lw_session_id', sid);
  }
  return sid;
};

/**
 * Derive a human-readable page title from the route path
 */
const getPageTitle = (path) => {
  const titles = {
    '/dashboard': 'Dashboard',
    '/customers': 'Customers',
    '/customers/add': 'Add Customer',
    '/reminders': 'Reminders',
    '/reminders/add': 'Add Reminder',
    '/invoices': 'Invoices',
    '/invoices/create': 'Create Invoice',
    '/email-templates': 'Email Templates',
    '/professional-tasks': 'Professional Tasks',
    '/reports': 'Reports',
    '/apps-ecommerce-orders': 'Orders',
    '/profile': 'Profile',
    '/settings': 'Settings',
    '/pricing': 'Pricing',
    '/plans': 'Plans',
    '/support': 'Support',
    '/notifications': 'Notifications',
    '/whatsapp': 'WhatsApp',
    '/website-builder': 'Website Builder',
    '/team': 'Team',
  };

  // Exact match
  if (titles[path]) return titles[path];

  // Partial match (e.g. /customers/123, /reminders/456/edit)
  for (const [key, title] of Object.entries(titles)) {
    if (path.startsWith(key)) return title;
  }

  // Admin pages
  if (path.startsWith('/admin')) {
    const parts = path.replace('/admin', '').split('/').filter(Boolean);
    if (parts.length === 0) return 'Admin Dashboard';
    return 'Admin - ' + parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  }

  // Fallback: capitalize the path segments
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0) {
    return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')).join(' > ');
  }

  return 'Unknown Page';
};

/**
 * Hook that tracks page visits and sends them to the backend.
 * Uses batching: collects page views and flushes every 10 seconds or on unload.
 * Only tracks if the user is authenticated.
 */
const useActivityTracker = () => {
  const location = useLocation();
  const bufferRef = useRef([]);
  const timerRef = useRef(null);

  const isAuthenticated = () => {
    const authUser = sessionStorage.getItem('authUser');
    return authUser && JSON.parse(authUser)?.token;
  };

  const flush = useCallback(() => {
    if (bufferRef.current.length === 0) return;
    if (!isAuthenticated()) {
      bufferRef.current = [];
      return;
    }

    const activities = [...bufferRef.current];
    bufferRef.current = [];

    // Fire and forget — don't block user experience
    api.create('/activity/track-batch', { activities }).catch(() => {
      // Silently ignore tracking failures
    });
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!isAuthenticated()) return;

    // Don't track admin routes for admin users viewing (they're tracked server-side)
    // But DO track if a non-admin visits admin pages (should be blocked anyway)
    
    const activity = {
      activityType: 'PAGE_VIEW',
      pagePath: location.pathname,
      pageTitle: getPageTitle(location.pathname),
      sessionId: getSessionId(),
      timestamp: new Date().toISOString().replace('Z', ''),
    };

    bufferRef.current.push(activity);

    // If buffer has 5+ items, flush immediately
    if (bufferRef.current.length >= 5) {
      flush();
    }
  }, [location.pathname, flush]);

  // Set up interval to flush buffer
  useEffect(() => {
    timerRef.current = setInterval(flush, 10000); // Every 10 seconds

    // Flush on page unload (beforeunload)
    const handleUnload = () => {
      if (bufferRef.current.length === 0 || !isAuthenticated()) return;
      const activities = [...bufferRef.current];
      bufferRef.current = [];
      // Use sendBeacon for reliable delivery on unload
      const blob = new Blob([JSON.stringify({ activities })], { type: 'application/json' });
      const authUser = JSON.parse(sessionStorage.getItem('authUser') || '{}');
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'https://api.leadwix.com/api'
        : 'https://api.leadwix.com/api';
      
      // sendBeacon doesn't support custom headers, so fall back to fetch with keepalive
      try {
        fetch(`${apiUrl}/activity/track-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({ activities }),
          keepalive: true,
        });
      } catch (e) {
        // Last resort: sendBeacon (no auth token, but server can extract user from session)
        navigator.sendBeacon?.(`${apiUrl}/activity/track-batch`, blob);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener('beforeunload', handleUnload);
      flush(); // Flush remaining on unmount
    };
  }, [flush]);
};

/**
 * Track a specific user action (button click, modal open, etc.)
 * Call this imperatively from any component:
 *   trackAction('BUTTON_CLICK', '/pricing', 'Clicked Upgrade to PRO');
 */
export const trackAction = (activityType, pagePath, details) => {
  const authUser = sessionStorage.getItem('authUser');
  if (!authUser) return;

  const activity = {
    activityType,
    pagePath: pagePath || window.location.pathname,
    pageTitle: getPageTitle(pagePath || window.location.pathname),
    details,
    sessionId: getSessionId(),
  };

  // Send immediately for important actions
  api.create('/activity/track', activity).catch(() => {});
};

export default useActivityTracker;
